import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import {
  CompanyLocationExceptionPreviewOutDTO,
  CompanyLocationsDetailOutDTO,
  ExceptionTimeIntervalOutDTO,
  LocationListPreviewOutDTO,
  Pageable,
} from '@api/ringil-api.v2';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SectionEmptyState from './SectionEmptyState.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import NewExceptionSidebar from '@app/pages/customer/exceptions/sidebars/NewExceptionSidebar.tsx';
import {
  dayjsFromApiUtcInTz,
  formatDateFromDayjsWithLocale,
  formatDateFromStringWithLocale,
  formatTimeFromDayjsWithLocale,
} from '@utils/dateWithTz';
import { exceptionsInWindow, matchingExceptionYmds } from './exceptionsWindow.ts';
import SvgClose from '@icons/close.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  exceptions: CompanyLocationExceptionPreviewOutDTO[] | undefined;
  onReload: () => void;
}

// RingilDataTable keys rows by `id`; exceptions expose locationExceptionId
type ExceptionRow = CompanyLocationExceptionPreviewOutDTO & { id: string };

const ExceptionsSection: React.FC<ComponentProps> = ({ location, exceptions, onReload }) => {
  const { tr } = useTranslationLgs();
  const { deleteCompanyLocationExceptions } = useContext(ApiContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);
  const [visibleNewException, setVisibleNewException] = useState(false);
  const [deleteId, setDeleteId] = useState<string>();

  const tz = location.timezoneId ?? 'Europe/Prague';
  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const readOnly = location.isDeleted;

  useEffect(() => {
    tableRef.current?.reloadData();
  }, [exceptions]);

  const matchingYmds = (row: CompanyLocationExceptionPreviewOutDTO) => matchingExceptionYmds(row, tz);
  const visibleExceptions = exceptionsInWindow(exceptions, tz);

  const loadData = (_filter: unknown, pageable: Pageable, onSuccess: (pagedData: PagedData<ExceptionRow>) => void) => {
    const page = pageable.pageNumber || 1;
    const size = pageable.pageSize || 10;
    const start = (page - 1) * size;
    const rows = visibleExceptions.map(t => ({ ...t, id: t.locationExceptionId }));
    onSuccess({ data: rows.slice(start, start + size), total: rows.length, page, size });
  };

  const confirmDelete = () => {
    deleteCompanyLocationExceptions([deleteId], () => {
      setDeleteId(undefined);
      onReload();
    });
  };

  const renderOpeningTime = (isClosed: boolean, openingTime?: ExceptionTimeIntervalOutDTO[]) => {
    if (isClosed) return tr('ExceptionList.closed', 'Closed');
    return (openingTime ?? [])
      .map(item =>
        item.localStartTime && item.localEndTime
          ? `${item.localStartTime} - ${item.localEndTime}`
          : `${formatTimeFromDayjsWithLocale(dayjsFromApiUtcInTz(item.since, tz))} - ${formatTimeFromDayjsWithLocale(dayjsFromApiUtcInTz(item.till, tz))}`,
      )
      .join(', ');
  };

  const renderLocalities = (localities: LocationListPreviewOutDTO[]) => {
    if (localities.length === 0) return tr('LocalityDetail.allOurLocations', 'All our locations');
    return localities.map((item, index) => <div key={index}>{item.localityName}</div>);
  };

  const columnsConfig: ColumnConfig<ExceptionRow>[] = [
    {
      id: 'dateFrom',
      field: 'date',
      header: 'LocalityDetail.dateFrom',
      headerDefault: 'Date from',
      width: '14%',
      sortable: false,
      order: 1,
      render: row => <div>{formatDateFromStringWithLocale(matchingYmds(row)[0])}</div>,
    },
    {
      id: 'exception',
      field: 'exception',
      header: 'LocalityDetail.exception',
      headerDefault: 'Exception',
      width: '28%',
      sortable: false,
      order: 2,
      render: row => (
        <div>
          <div>{row.exceptionName}</div>
          <div>{renderOpeningTime(row.closed, row.openingHours)}</div>
        </div>
      ),
    },
    {
      id: 'locations',
      field: 'locations',
      header: 'LocalityDetail.locality',
      headerDefault: 'Locality',
      width: '22%',
      sortable: false,
      order: 3,
      render: row => <div>{renderLocalities(row.localities)}</div>,
    },
    {
      id: 'ramps',
      field: 'ramps',
      header: 'LocalityDetail.ramps',
      headerDefault: 'Ramps',
      width: '16%',
      sortable: false,
      order: 4,
      render: row => {
        // preview DTO has no locality id; name match is the only correlation
        const entry = row.localities?.find(l => l.localityName === location.name);
        if (!row.localities || row.localities.length === 0 || !entry?.rampsNames?.length) {
          return <span>{tr('LocalityDetail.allRamps', 'All ramps')}</span>;
        }
        return <span>{entry.rampsNames.join(', ')}</span>;
      },
    },
    {
      id: 'createdBy',
      field: 'createdAt',
      header: 'LocalityDetail.createdBy',
      headerDefault: 'Created by',
      width: '14%',
      sortable: false,
      order: 5,
      render: row => (
        <div>
          <div>{row.createdBy}</div>
          <div>{formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(row.createdAt, tz))}</div>
        </div>
      ),
    },
    {
      id: 'action',
      field: 'action',
      width: '6%',
      sortable: false,
      order: 6,
      render: row =>
        readOnly ? null : (
          <RingilButton
            mode={'table'}
            icon={<SvgDelete />}
            onClick={() => setDeleteId(row.locationExceptionId)}
            dataTestId={`location-exception-delete-${row.locationExceptionId}`}
          />
        ),
    },
  ];

  const heading = (
    <span className="flex flex-row items-center gap-2">
      {tr('LocalityDetail.exceptionsInNext30Days', 'Exceptions in next 30 days')}
      {!readOnly && (
        <RingilButton
          mode={'link'}
          icon={<SvgPlus />}
          label={tr('ExceptionList.newException', 'New Exception')}
          onClick={() => setVisibleNewException(true)}
          dataTestId={'location-exceptions-new'}
        />
      )}
    </span>
  );

  return (
    <>
      <div>
        <SectionPanel
          icon={<SvgClose className={'w-6 h-6'} />}
          iconColor={'var(--content-tertiary)'}
          heading={heading}
          headerRight={<COTimezone timezoneId={location.timezoneId} dataTestId={'location-exceptions-timezone'} />}>
          {visibleExceptions.length > 0 ? (
            <RingilDataTable
              ref={tableRef}
              columnConfig={columnsConfig as ColumnConfig<unknown>[]}
              loadDataFromApi={loadData}
              filterName={'location_detail_exceptions'}
            />
          ) : (
            exceptions != null && (
              <SectionEmptyState
                message={tr('LocalityDetail.noExceptionSet', 'No exception is set.')}
                buttonLabel={readOnly ? undefined : tr('LocalityDetail.setException', 'Set exception')}
                onButtonClick={readOnly ? undefined : () => setVisibleNewException(true)}
                dataTestId={'location-exceptions-empty'}
              />
            )
          )}
        </SectionPanel>
      </div>

      <NewExceptionSidebar
        visible={visibleNewException}
        onHide={() => setVisibleNewException(false)}
        onAdded={() => {
          setVisibleNewException(false);
          onReload();
        }}
        preselectLocationId={location.id}
      />

      <ConfirmDialog
        visible={deleteId != null}
        dialogTitle={tr('ExceptionList.confirmDeleteActions', 'Confirm delete actions')}
        dialogMessage={tr('ExceptionList.doYouWantToDeleteException', 'Do you want to delete exception?')}
        onCancel={() => setDeleteId(undefined)}
        onConfirm={confirmDelete}
        onAcceptButtonText={tr('ExceptionList.confirm', 'Confirm')}
        onCancelButtonText={tr('ExceptionList.back', 'Back')}
      />
    </>
  );
};

export default ExceptionsSection;
